import { Children, memo, useCallback, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Button,
  Card,
  SearchableSelect,
  Skeleton,
  StatusMessage,
  useToast,
} from '@aimsos/ui';
import { Trans, useTranslation } from '@aimsos/i18n';
import { useInstanceAccess } from '../../hooks/instance';
import AccessRemoveModal from './AccessRemoveModal.jsx';
import { CHIP_BASE, CHIP_LABEL, CHIP_TONE, initialsOf } from '../../utils';
import { AlertTriangle, Plus, Users, X } from 'lucide-react';

const AVATAR_CLASS = [
  '!h-[30px] !w-[30px] !min-h-[30px] !min-w-[30px] !border-0',
  'text-white [&>span]:text-[11px] [&>span]:font-bold [&>span]:leading-none [&>span]:text-white',
].join(' ');

const GROUP_AVATAR_CLASS = [
  'inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full',
  'border border-[rgba(43,127,255,0.28)] bg-[rgba(43,127,255,0.12)] text-[#2B7FFF]',
].join(' ');

const AccessWhoRow = memo(function AccessWhoRow({ entry, onRemove, removeLabel }) {
  const isGroup = entry.type === 'group';

  return (
    <div className="flex items-center gap-[11px] rounded-lg border border-white/[0.05] bg-white/[0.022] px-[11px] py-[9px] transition-colors hover:border-white/10 hover:bg-white/[0.04]">
      {isGroup ? (
        <span className={GROUP_AVATAR_CLASS} aria-hidden="true">
          <Users size={14} strokeWidth={1.8} />
        </span>
      ) : (
        <Avatar
          alt={entry.name}
          initials={entry.initials || initialsOf(entry.name)}
          className={`${AVATAR_CLASS} ${entry.avatarClassName ?? 'bg-[#2B7FFF]'}`}
        />
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold tracking-[-0.005em] text-content">{entry.name}</p>
        {entry.subtitle ? (
          <p className="truncate text-[11.5px] text-content-faint">{entry.subtitle}</p>
        ) : null}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        leftIcon={X}
        aria-label={removeLabel}
        onClick={() => onRemove(entry)}
        className="!h-6 !min-h-6 !w-6 !min-w-6 !rounded-md !p-0 !text-content-faint hover:!bg-[rgba(239,68,68,0.10)] hover:!text-[#ff7d7d] [&_svg]:!m-0"
      />
    </div>
  );
});

function AccessSection({ label, count, partial, empty, children }) {
  const items = Children.toArray(children);
  return (
    <section className="mb-[18px] last:mb-3.5">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-content-secondary">{label}</span>
        <Badge
          variant="neutral"
          size="small"
          className={`${CHIP_BASE} ${
            partial
              ? '!border-[rgba(251,191,36,0.30)] !bg-[rgba(251,191,36,0.12)] !text-[#fbbf24]'
              : CHIP_TONE.neutral
          }`}
        >
          <span className={CHIP_LABEL}>{count}</span>
        </Badge>
      </div>
      <div className="flex flex-col gap-1.5">
        {items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-white/[0.08] bg-white/[0.02] px-3.5 py-3.5 text-center text-xs leading-normal text-content-faint">
            {empty}
          </div>
        ) : (
          items
        )}
      </div>
    </section>
  );
}

/**
 * Instance Detail — Access tab (CT-4191, admin-only). Prototype `instanceAccessHTML`:
 * where it appears (workspaces) and who can use it (people, groups).
 */
const AccessTab = () => {
  const { t } = useTranslation('admin-studio');
  const toast = useToast();
  const { instance } = useOutletContext();
  const { entries, directory, workspaceTotal, reachCount, isLoading, error, grant, remove } =
    useInstanceAccess(instance.instanceId);
  const [pendingRemove, setPendingRemove] = useState(null);

  const workspaces = useMemo(() => entries.filter((e) => e.type === 'workspace'), [entries]);
  const people = useMemo(() => entries.filter((e) => e.type === 'user'), [entries]);
  const groups = useMemo(() => entries.filter((e) => e.type === 'group'), [entries]);

  const pickerOptions = useMemo(
    () =>
      directory.map((opt) => ({
        ...opt,
        initial: opt.initial || initialsOf(opt.name),
      })),
    [directory],
  );

  const notifyDistribution = useCallback(
    (event) => {
      event.preventDefault();
      toast.info({ message: t('integrations.provider.stubs.distribution'), canClose: true });
    },
    [t, toast],
  );

  const onGrant = useCallback(
    (option) => {
      if (!option) return;
      grant(option, 'use');
    },
    [grant],
  );

  const confirmRemove = useCallback(() => {
    if (!pendingRemove) return;
    remove(pendingRemove.id);
    setPendingRemove(null);
  }, [pendingRemove, remove]);

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (error) return <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />;

  const wsPartial = workspaces.length !== workspaceTotal;

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-[18px] pb-2.5 pt-3.5">
        <h3 className="text-sm font-semibold tracking-[-0.005em] text-content">
          {t('integrations.instance.access.title', { name: instance.name })}
        </h3>
        <p className="shrink-0 text-[11px] font-medium text-content-faint">
          {t('integrations.instance.access.reach', { count: reachCount })}
        </p>
      </div>

      <Card.Body className="px-[18px] pb-[18px] pt-0">
        <p className="mb-3.5 text-[12.5px] leading-[1.55] text-content-secondary">
          <Trans
            ns="admin-studio"
            i18nKey="integrations.instance.access.intro"
            components={{
              where: <strong className="font-semibold text-content" />,
              who: <strong className="font-semibold text-content" />,
              dist: (
                <button
                  type="button"
                  className="font-medium text-[#2B7FFF] hover:underline"
                  onClick={notifyDistribution}
                />
              ),
            }}
          />
        </p>

        <AccessSection
          label={t('integrations.instance.access.sections.workspaces')}
          count={`${workspaces.length} / ${workspaceTotal}`}
          partial={wsPartial}
          empty={t('integrations.instance.access.empty.workspaces')}
        >
          {workspaces.map((entry) => (
            <AccessWhoRow
              key={entry.id}
              entry={entry}
              onRemove={setPendingRemove}
              removeLabel={t('integrations.instance.access.removeNamed', { name: entry.name })}
            />
          ))}
        </AccessSection>

        <AccessSection
          label={t('integrations.instance.access.sections.people')}
          count={String(people.length)}
          empty={t('integrations.instance.access.empty.people')}
        >
          {people.map((entry) => (
            <AccessWhoRow
              key={entry.id}
              entry={entry}
              onRemove={setPendingRemove}
              removeLabel={t('integrations.instance.access.removeNamed', { name: entry.name })}
            />
          ))}
        </AccessSection>

        <AccessSection
          label={t('integrations.instance.access.sections.groups')}
          count={String(groups.length)}
          empty={t('integrations.instance.access.empty.groups')}
        >
          {groups.map((entry) => (
            <AccessWhoRow
              key={entry.id}
              entry={entry}
              onRemove={setPendingRemove}
              removeLabel={t('integrations.instance.access.removeNamed', { name: entry.name })}
            />
          ))}
        </AccessSection>

        <div className="mt-1.5 flex flex-wrap items-center gap-3.5">
          <SearchableSelect
            options={pickerOptions}
            onSelect={onGrant}
            placeholder={t('integrations.instance.access.add')}
            searchPlaceholder={t('integrations.instance.access.search')}
            menuTitle={t('integrations.instance.access.sections.people')}
            emptyMessage={t('integrations.instance.access.pickerEmpty')}
            menuListMaxHeight="16rem"
            menuClassName="!w-[340px]"
            renderTrigger={({ isOpen, disabled }) => (
              <Button
                type="button"
                variant="classicPrimary"
                size="sm"
                leftIcon={Plus}
                disabled={disabled}
                aria-haspopup="menu"
                aria-expanded={isOpen}
              >
                {t('integrations.instance.access.add')}
              </Button>
            )}
          />
          <p className="min-w-0 flex-1 text-[11px] text-content-faint">
            {t('integrations.instance.access.removeHint')}
          </p>
        </div>
      </Card.Body>

      {pendingRemove ? (
        <AccessRemoveModal
          instanceId={instance.instanceId}
          entry={pendingRemove}
          onClose={() => setPendingRemove(null)}
          onConfirm={confirmRemove}
        />
      ) : null}
    </Card>
  );
};

export default AccessTab;
